import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { TokenType } from '@wix/ambassador-asa-token-v1-token/types';
import { isValidCalendarToken } from '../../entities/CalendarToken';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
import { apiProviderToCalendarProvider, calendarProviderToApiProvider } from './converters';
export class CalendarTokenClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "listManageableTokensImpl", void 0);
    _defineProperty(this, "getTokenImpl", void 0);
    _defineProperty(this, "queryTokensImpl", void 0);
    _defineProperty(this, "upsertTokenImpl", void 0);
    _defineProperty(this, "markTokenAsManageableImpl", void 0);
    _defineProperty(this, "unMarkTokenAsManageableImpl", void 0);
    _defineProperty(this, "deleteTokenForContactImpl", void 0);
    _defineProperty(this, "updateManageableTokenGuidelinesImpl", void 0);
    _defineProperty(this, "isManageableTokenImpl", void 0);
  }
  getServiceNameForLogs() {
    return 'CalendarTokenClient';
  }
  async listManageableTokens(paging) {
    try {
      var _result$tokens;
      this.logInfo('listTokens', 'entry', {
        paging
      });
      for (var _len = arguments.length, extraParam = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        extraParam[_key - 1] = arguments[_key];
      }
      const result = await this.request(this.listManageableTokensImpl({
        paging
      }), ...extraParam);
      const items = result.tokens ?? [];
      this.logInfo('listTokens', 'result', {
        pagingMetadata: result.pagingMetadata,
        result: (_result$tokens = result.tokens) == null ? void 0 : _result$tokens.map(token => {
          return {
            id: token.id,
            provider: token.provider
          };
        })
      });
      return {
        items: items.map(token => this.convertToEntity(token)),
        ...pagingMetadataToCursorResponse(result.pagingMetadata)
      };
    } catch (error) {
      this.logError('listTokens', 'Failed to list tokens', {
        paging
      }, error);
      throw error;
    }
  }
  async getTokensForContacts(contactIds) {
    try {
      for (var _len2 = arguments.length, extraParam = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        extraParam[_key2 - 1] = arguments[_key2];
      }
      const {
        tokens
      } = await this.request(this.queryTokensImpl({
        contactIds
      }), ...extraParam);
      if (!tokens) {
        throw new Error('Received invalid tokens');
      }
      return tokens.map(token => this.convertToEntity(token));
    } catch (error) {
      this.logError('getTokenByContactId', 'Failed to get tokens for contacts', {
        contactIds
      }, error);
      throw error;
    }
  }
  async getTokenForContact(contactId) {
    for (var _len3 = arguments.length, extraParam = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      extraParam[_key3 - 1] = arguments[_key3];
    }
    const tokens = await this.getTokensForContacts([contactId], ...extraParam);
    return tokens.length > 0 ? tokens[0] : null;
  }
  async getTokenById(tokenId) {
    try {
      for (var _len4 = arguments.length, extraParam = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        extraParam[_key4 - 1] = arguments[_key4];
      }
      const {
        token
      } = await this.request(this.getTokenImpl({
        tokenId
      }), ...extraParam);
      return this.convertToEntity(token);
    } catch (error) {
      this.logError('getTokenById', 'Failed to get token by ID', {
        tokenId
      }, error);
      throw error;
    }
  }
  async upsertToken(tokenData, contactId) {
    try {
      for (var _len5 = arguments.length, extraParam = new Array(_len5 > 2 ? _len5 - 2 : 0), _key5 = 2; _key5 < _len5; _key5++) {
        extraParam[_key5 - 2] = arguments[_key5];
      }
      const {
        token
      } = await this.request(this.upsertTokenImpl({
        token: {
          tokenType: TokenType.BEARER,
          email: tokenData.email,
          grantId: tokenData.grantId,
          provider: calendarProviderToApiProvider(tokenData.provider),
          accessToken: tokenData.accessToken,
          refreshToken: tokenData.refreshToken,
          scope: tokenData.scope
        },
        contactId
      }), ...extraParam);
      return this.convertToEntity(token);
    } catch (error) {
      this.logError('upsertToken', 'Failed to upsert token', {
        tokenData
      }, error);
      throw error;
    }
  }
  async markTokenAsManageable(tokenId) {
    try {
      for (var _len6 = arguments.length, extraParam = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        extraParam[_key6 - 1] = arguments[_key6];
      }
      await this.request(this.markTokenAsManageableImpl({
        tokenId
      }), ...extraParam);
    } catch (error) {
      this.logError('markTokenAsManageable', 'Failed to mark token as manageable', {
        tokenId
      }, error);
      throw error;
    }
  }
  async unMarkTokenAsManageable(tokenId) {
    try {
      for (var _len7 = arguments.length, extraParam = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
        extraParam[_key7 - 1] = arguments[_key7];
      }
      await this.request(this.unMarkTokenAsManageableImpl({
        tokenId
      }), ...extraParam);
    } catch (error) {
      this.logError('unMarkTokenAsManageable', 'Failed to unmark token as manageable', {
        tokenId
      }, error);
      throw error;
    }
  }
  async deleteTokenForContact(contactId) {
    try {
      for (var _len8 = arguments.length, extraParam = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
        extraParam[_key8 - 1] = arguments[_key8];
      }
      await this.request(this.deleteTokenForContactImpl({
        contactId
      }), ...extraParam);
    } catch (error) {
      this.logError('deleteTokenForClient', 'Failed to delete contact token', {
        contactId
      }, error);
      throw error;
    }
  }
  async updateManageableTokenGuidelines(tokenId, guidelines) {
    try {
      for (var _len9 = arguments.length, args = new Array(_len9 > 2 ? _len9 - 2 : 0), _key9 = 2; _key9 < _len9; _key9++) {
        args[_key9 - 2] = arguments[_key9];
      }
      await this.request(this.updateManageableTokenGuidelinesImpl({
        tokenId,
        guidelines
      }), ...args);
    } catch (error) {
      this.logError('updateManageableTokenGuidelines', 'Failed to update guidelines', {
        tokenId
      }, error);
      throw error;
    }
  }
  async isManageableToken(tokenId) {
    try {
      for (var _len0 = arguments.length, args = new Array(_len0 > 1 ? _len0 - 1 : 0), _key0 = 1; _key0 < _len0; _key0++) {
        args[_key0 - 1] = arguments[_key0];
      }
      const {
        manageable
      } = await this.request(this.isManageableTokenImpl({
        tokenId
      }), ...args);
      return Boolean(manageable);
    } catch (error) {
      this.logError('isManageableToken', 'Failed to check if token is manageable', {
        tokenId
      }, error);
      throw error;
    }
  }
  convertToEntity(token) {
    try {
      if (!token) {
        throw new Error('Invalid token', {
          cause: {
            token
          }
        });
      }
      const convertedToken = {
        ...token,
        provider: apiProviderToCalendarProvider(token.provider)
      };
      if (isValidCalendarToken(convertedToken)) {
        return convertedToken;
      }
      throw new Error('Invalid Token data', {
        cause: {
          token
        }
      });
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert token', {
        token
      }, error);
      throw error;
    }
  }
}
//# sourceMappingURL=CalendarTokenClientBase.js.map